import {
  useContext,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useColorModeValue,
} from '@chakra-ui/react';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import ComingSoon from 'views/coming_soon';
import { AnalyticsHeader } from './components/AnalyticsHeader';

import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import Card from 'components/card/Card';
import {
  apiGetSalesAnalytics,
  apiGetAnalyticsCallList,
  apiGetDrillDown,
  apiGetLeadManagement,
  apiGetAnalyticsOverview,
} from 'api';
import type { APIAnalyticsOverview } from 'types/api.types';
import { AnalyticsDashboardOverview } from './components/AnalyticsDashboardOverview';
import { AuthContext } from 'contexts/AuthContext';
import {
  ROLE_ADMIN,
  ROLE_MANAGER,
  ROLE_BROKER,
  ROLE_BROKER_PARTNER,
} from 'variables/constants';
import { computeAggregateKPI } from 'utils/aggregateMetrics';
import type { EntityRow } from 'utils/aggregateMetrics';

import { ModeToggle } from './components/ModeToggle';
import { LoadingCard } from 'components/shared/LoadingCard';
import { ErrorAlert } from 'components/shared/ErrorAlert';
import { AnalyticsFilters } from './components/AnalyticsFilters';
import { OverallKPICards } from './components/OverallKPICards';
import { AggregateKPIBar } from './components/AggregateKPIBar';
import {
  BranchBrokerTable,
  AggregateBrokerTable,
} from './components/BranchBrokerTable';
import type {
  BranchRow,
  BrokerRow,
  AggregateBrokerRow,
} from './components/BranchBrokerTable';
import { CallListTable, type CallRow } from './components/CallListTable';
import { DetailPanel } from './components/DetailPanel';
import { LeadManagementPanel } from './components/LeadManagementPanel';

function currentYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const AnalyticsViewContent = () => {
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  // Persist detail panel in URL so it survives reload
  const detailType = searchParams.get('detail_type') as
    | 'branch'
    | 'broker'
    | null;
  const detailId = searchParams.get('detail_id');
  const detailTarget: { type: 'branch' | 'broker'; id: string } | null =
    detailType && detailId ? { type: detailType, id: detailId } : null;
  const setDetailTarget = useCallback(
    (target: { type: 'branch' | 'broker'; id: string } | null) => {
      setSearchParams(
        (p) => {
          if (target) {
            p.set('detail_type', target.type);
            p.set('detail_id', target.id);
          } else {
            p.delete('detail_type');
            p.delete('detail_id');
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const queryClient = useQueryClient();
  const [perfSortKey, setPerfSortKey] = useState<
    'score' | 'quotes' | 'closing_ratio' | 'nb_premium_total' | 'p2p_percent'
  >('score');
  const [perfSortDir, setPerfSortDir] = useState<'asc' | 'desc'>('desc');
  const OUTER_TAB_COUNT = 3;
  const SALES_TAB_COUNT = 3;
  const outerTabIndex = Math.max(
    0,
    Math.min(OUTER_TAB_COUNT - 1, Number(searchParams.get('tab')) || 0),
  );
  const setOuterTabIndex = useCallback(
    (i: number) => {
      setSearchParams(
        (p) => {
          p.set('tab', String(i));
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const salesTabIndex = Math.max(
    0,
    Math.min(SALES_TAB_COUNT - 1, Number(searchParams.get('stab')) || 0),
  );
  const setSalesTabIndex = useCallback(
    (i: number) => {
      setSearchParams(
        (p) => {
          p.set('stab', String(i));
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const isAdminOrManager =
    user?.role === ROLE_ADMIN || user?.role === ROLE_MANAGER;
  const isBrokerLevel =
    user?.role === ROLE_BROKER || user?.role === ROLE_BROKER_PARTNER;
  const bgColor = useColorModeValue('gray.50', 'navy.900');

  const yearMonth = searchParams.get('month') ?? currentYearMonth();
  const supportsPeriod =
    outerTabIndex === 0 ||
    (outerTabIndex === 1 && (salesTabIndex === 0 || salesTabIndex === 2));
  const period = supportsPeriod
    ? (searchParams.get('period') ?? 'monthly')
    : 'monthly';
  const selectedBranchId = searchParams.get('branch')
    ? Number(searchParams.get('branch'))
    : null;
  const selectedBrokerEmail = searchParams.get('broker') ?? null;

  const viewMode = (searchParams.get('mode') ?? 'overview') as
    | 'overview'
    | 'aggregate';
  const setViewMode = useCallback(
    (m: 'overview' | 'aggregate') => {
      setSearchParams(
        (p) => {
          p.set('mode', m);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const setYearMonth = (v: string) =>
    setSearchParams(
      (p) => {
        p.set('month', v);
        p.delete('period');
        return p;
      },
      { replace: true },
    );

  const setPeriod = (v: string) =>
    setSearchParams(
      (p) => {
        p.set('period', v);
        p.set('month', currentYearMonth());
        return p;
      },
      { replace: true },
    );

  const handleBranchSelect = (id: number | null) => {
    setSearchParams(
      (p) => {
        if (id) p.set('branch', String(id));
        else p.delete('branch');
        // Clear broker filter when branch changes
        p.delete('broker');
        return p;
      },
      { replace: true },
    );
  };

  const handleBrokerFilter = (email: string | null) => {
    setSearchParams(
      (p) => {
        if (email) {
          p.set('broker', email);
          // Auto-select the branch this broker belongs to (CR 8.1 — single source of truth)
          const parentBranch = (salesData?.broker_table ?? []).find((b: any) =>
            (b.children ?? []).some((c: any) => c.email === email),
          );
          if (parentBranch?.branch_id)
            p.set('branch', String(parentBranch.branch_id));
        } else {
          p.delete('broker');
        }
        return p;
      },
      { replace: true },
    );
    if (email && viewMode === 'overview') {
      setDetailTarget({ type: 'broker', id: email });
    }
  };

  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup pending hover timer on unmount
  useEffect(
    () => () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    },
    [],
  );

  const handleBranchHover = (id: number) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: ['drill-down', 'branch', String(id), yearMonth],
        queryFn: () =>
          apiGetDrillDown({ type: 'branch', id: String(id), month: yearMonth }),
        staleTime: 10 * 60 * 1000,
      });
    }, 200);
  };

  const handleBrokerHover = (email: string) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: ['drill-down', 'broker', email, yearMonth],
        queryFn: () =>
          apiGetDrillDown({ type: 'broker', id: email, month: yearMonth }),
        staleTime: 10 * 60 * 1000,
      });
    }, 200);
  };

  const handleRemoveChip = (id: string) => {
    const next = new Set(selectedIds);
    next.delete(id);
    if (id.startsWith('branch:')) {
      const branchId = Number(id.replace('branch:', ''));
      const branch = (salesData?.broker_table ?? []).find(
        (b: BranchRow) => b.branch_id === branchId,
      );
      (branch?.children ?? []).forEach((b: BrokerRow) =>
        next.delete(`broker:${b.email}`),
      );
    }
    setSelectedIds(next);
  };

  const viewLabel =
    user?.role === ROLE_ADMIN
      ? 'All Branches & Brokers'
      : user?.role === ROLE_MANAGER
        ? 'Your Assigned Branches'
        : 'Your Performance';

  // ── Data queries ──────────────────────────────────────────────────────────────

  const {
    data: salesData,
    isLoading: salesLoading,
    error: salesError,
  } = useQuery({
    queryKey: ['sales-analytics', yearMonth, period],
    queryFn: () => apiGetSalesAnalytics({ year_month: yearMonth, period }),
    staleTime: 10 * 60 * 1000, // 10 min — reuse in-memory data when switching between months
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
  }) as { data: any; isLoading: boolean; error: any };

  const {
    data: callListData,
    isLoading: callListLoading,
    error: callListError,
  } = useQuery({
    queryKey: [
      'analytics-call-list',
      yearMonth,
      period,
      selectedBranchId,
      selectedBrokerEmail,
    ],
    queryFn: () =>
      apiGetAnalyticsCallList({
        year_month: yearMonth,
        period,
        branch_id: selectedBranchId,
        broker_email: selectedBrokerEmail,
      }),
    staleTime: 5 * 60 * 1000, // 5 min — call list data changes frequently
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    enabled: salesTabIndex === 2, // lazy-load: only fetch when Call List tab is active
  }) as { data: any; isLoading: boolean; error: any };

  const { data: leadMgmtData, isLoading: leadMgmtLoading } = useQuery({
    queryKey: ['analytics-lead-management', yearMonth],
    queryFn: () => apiGetLeadManagement({ year_month: yearMonth }),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 300000,
    enabled: salesTabIndex === 1,
  }) as { data: any; isLoading: boolean };

  const { data: leadTrendData } = useQuery({
    queryKey: [
      'analytics-overview',
      yearMonth,
      'monthly',
      selectedBranchId,
      selectedBrokerEmail,
      null,
      null,
    ],
    queryFn: () =>
      apiGetAnalyticsOverview({
        year_month: yearMonth,
        period: 'monthly',
        branch_id: selectedBranchId,
        broker_email: selectedBrokerEmail,
      }) as Promise<APIAnalyticsOverview>,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: salesTabIndex === 1,
  });

  // ── Auto-select on initial load (per month) ──────────────────────────────────
  const autoSelectedMonthRef = useRef<string | null>(null);

  useEffect(() => {
    if (!salesData?.broker_table?.length || viewMode !== 'overview') return;
    if (autoSelectedMonthRef.current === yearMonth) return;
    // Don't override a detail panel that's already in the URL (reloaded state)
    if (detailTarget) {
      autoSelectedMonthRef.current = yearMonth;
      return;
    }

    if (isBrokerLevel && user?.email) {
      // Broker / Broker Partner: open own drill-down
      setDetailTarget({ type: 'broker', id: user.email });
    }
    // Admin/Manager: accordion auto-expands via defaultBranchId — no DetailPanel needed
    autoSelectedMonthRef.current = yearMonth;
  }, [salesData, yearMonth, viewMode, isBrokerLevel, user?.email]);

  // ── Derived data ──────────────────────────────────────────────────────────────

  const branchOptions = isAdminOrManager
    ? (salesData?.broker_table ?? [])
        .map((b: any) => ({ id: b.branch_id, label: b.label }))
        .filter((b: any) => b.id != null)
        .filter(
          (b: any, i: number, arr: any[]) =>
            arr.findIndex((x: any) => x.id === b.id) === i,
        )
    : [];

  // Broker options: all brokers visible to the user, narrowed to selected branch if set
  const brokerOptions = useMemo(() => {
    if (!isAdminOrManager) return [];
    const sourceTable: BranchRow[] = selectedBranchId
      ? (salesData?.broker_table ?? []).filter(
          (b: any) => b.branch_id === selectedBranchId,
        )
      : (salesData?.broker_table ?? []);
    const opts: { email: string; label: string }[] = [];
    sourceTable.forEach((branch: BranchRow) => {
      (branch.children ?? []).forEach((broker) => {
        opts.push({ email: broker.email, label: broker.label });
      });
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [salesData, selectedBranchId, isAdminOrManager]);

  // Apply branch then broker filter
  const filteredTable: BranchRow[] = useMemo(() => {
    const byBranch: BranchRow[] = selectedBranchId
      ? (salesData?.broker_table ?? []).filter(
          (b: any) => b.branch_id === selectedBranchId,
        )
      : (salesData?.broker_table ?? []);

    if (!selectedBrokerEmail) return byBranch;

    // Find the branch containing the broker, return that branch with only that child
    for (const branch of byBranch) {
      const match = (branch.children ?? []).find(
        (b) => b.email === selectedBrokerEmail,
      );
      if (match) return [{ ...branch, children: [match] }];
    }
    return byBranch;
  }, [salesData, selectedBranchId, selectedBrokerEmail]);

  const selectedBranchRow = selectedBranchId
    ? (filteredTable[0] ?? null)
    : null;

  // Find the selected broker row (used for KPI cards when broker filter is active)
  const selectedBrokerRow: BrokerRow | null = useMemo(() => {
    if (!selectedBrokerEmail) return null;
    for (const branch of (salesData?.broker_table ?? []) as BranchRow[]) {
      const match = (branch.children ?? []).find(
        (b: BrokerRow) => b.email === selectedBrokerEmail,
      );
      if (match) return match;
    }
    return null;
  }, [salesData, selectedBrokerEmail]);

  // KPI priority: broker row > branch row > overall
  const kpiData = selectedBrokerRow ?? selectedBranchRow ?? salesData?.overall;

  const aggregateEntityRows: EntityRow[] = useMemo(() => {
    if (selectedIds.size === 0) return [];
    const rows: EntityRow[] = [];
    (salesData?.broker_table ?? []).forEach((branch: BranchRow) => {
      if (selectedIds.has(`branch:${branch.branch_id}`)) {
        rows.push(branch as unknown as EntityRow);
      } else {
        (branch.children ?? []).forEach((broker) => {
          if (selectedIds.has(`broker:${broker.email}`))
            rows.push(broker as unknown as EntityRow);
        });
      }
    });
    return rows;
  }, [selectedIds, salesData]);

  const aggregateKPI = useMemo(
    () => computeAggregateKPI(aggregateEntityRows),
    [aggregateEntityRows],
  );

  const aggregateBrokerRows = useMemo((): AggregateBrokerRow[] => {
    if (selectedIds.size === 0) return [];
    const rows: AggregateBrokerRow[] = [];
    (salesData?.broker_table ?? []).forEach((branch: BranchRow) => {
      (branch.children ?? []).forEach((broker) => {
        if (selectedIds.has(`broker:${broker.email}`)) {
          rows.push({ ...broker, branch_label: branch.label });
        }
      });
    });
    return rows;
  }, [selectedIds, salesData]);

  const aggregateChips = useMemo(() => {
    const chips: { id: string; label: string }[] = [];
    selectedIds.forEach((id) => {
      if (id.startsWith('branch:')) {
        const branchId = Number(id.replace('branch:', ''));
        const branch = (salesData?.broker_table ?? []).find(
          (b: BranchRow) => b.branch_id === branchId,
        );
        if (branch) chips.push({ id, label: branch.label });
      } else if (id.startsWith('broker:')) {
        const email = id.replace('broker:', '');
        for (const branch of (salesData?.broker_table ?? []) as BranchRow[]) {
          const broker = (branch.children ?? []).find((b) => b.email === email);
          if (broker) {
            chips.push({ id, label: `${broker.label} · ${branch.label}` });
            break;
          }
        }
      }
    });
    return chips;
  }, [selectedIds, salesData]);

  const selectedBranchIds: Set<number> = useMemo(() => {
    const ids = new Set<number>();
    if (viewMode === 'overview' && selectedBranchId) {
      ids.add(selectedBranchId);
    } else if (viewMode === 'aggregate') {
      selectedIds.forEach((id) => {
        if (id.startsWith('branch:'))
          ids.add(Number(id.replace('branch:', '')));
      });
      (salesData?.broker_table ?? []).forEach((branch: BranchRow) => {
        (branch.children ?? []).forEach((broker) => {
          if (
            selectedIds.has(`broker:${broker.email}`) &&
            branch.branch_id != null
          ) {
            ids.add(branch.branch_id);
          }
        });
      });
    }
    return ids;
  }, [viewMode, selectedBranchId, selectedIds, salesData]);

  // Default branch: top-scoring branch — auto-expanded in accordion on load
  const defaultBranchId = useMemo(() => {
    if (!isAdminOrManager || !salesData?.broker_table?.length) return null;
    const top = [...salesData.broker_table].sort(
      (a: any, b: any) => (b.score ?? 0) - (a.score ?? 0),
    )[0];
    return top?.branch_id ?? null;
  }, [salesData, isAdminOrManager]);

  // Active branch for accordion sync — tracks selected branch or broker's parent branch
  const activeBranchIdForTable: number | null = useMemo(() => {
    if (viewMode !== 'overview') return null;
    if (detailTarget?.type === 'branch') return Number(detailTarget.id);
    if (detailTarget?.type === 'broker') {
      const branch = (salesData?.broker_table ?? []).find((b: any) =>
        (b.children ?? []).some((c: any) => c.email === detailTarget.id),
      );
      return branch?.branch_id ?? defaultBranchId;
    }
    return defaultBranchId;
  }, [viewMode, detailTarget, salesData, defaultBranchId]);

  // Sort performance table (branch order drives call list order)
  const sortedTable: BranchRow[] = useMemo(
    () =>
      [...filteredTable].sort((a, b) => {
        const diff = (a as any)[perfSortKey] - (b as any)[perfSortKey];
        return perfSortDir === 'desc' ? -diff : diff;
      }),
    [filteredTable, perfSortKey, perfSortDir],
  );

  const branchOrderMap = useMemo(() => {
    const map = new Map<number, number>();
    sortedTable.forEach((b, i) => {
      if (b.branch_id != null) map.set(b.branch_id, i);
    });
    return map;
  }, [sortedTable]);

  const filteredCallList = useMemo(() => {
    const all = callListData?.call_list ?? [];

    // Branch filter
    let filtered =
      selectedBranchIds.size > 0
        ? all.filter(
            (b: any) =>
              b.branch_id != null && selectedBranchIds.has(b.branch_id),
          )
        : all;

    // Broker filter — narrow children to the selected broker
    if (selectedBrokerEmail) {
      filtered = filtered
        .map((branch: any) => ({
          ...branch,
          children: (branch.children ?? []).filter(
            (c: CallRow) => c.email === selectedBrokerEmail,
          ),
        }))
        .filter((branch: any) => (branch.children ?? []).length > 0);
    }

    return [...filtered].sort((a: any, b: any) => {
      const ai = branchOrderMap.get(a.branch_id) ?? 9999;
      const bi = branchOrderMap.get(b.branch_id) ?? 9999;
      return ai - bi;
    });
  }, [callListData, selectedBranchIds, selectedBrokerEmail, branchOrderMap]);

  // ── Table callbacks (typed ahead of JSX to avoid TS2590 union complexity) ────

  const tableOnBranchClick: ((id: number) => void) | undefined =
    isAdminOrManager && viewMode === 'overview'
      ? (id) => {
          handleBranchSelect(id); // CR 4.3.1 — sync dropdown
          setDetailTarget({ type: 'branch', id: String(id) });
        }
      : undefined;

  const tableOnBrokerClick: ((email: string) => void) | undefined =
    viewMode === 'overview'
      ? (email) => {
          if (isAdminOrManager || email === user?.email) {
            handleBrokerFilter(email); // CR 4.3.1 — sync dropdown
            setDetailTarget({ type: 'broker', id: email });
          }
        }
      : undefined;

  const tableOnBranchHover: ((id: number) => void) | undefined =
    isAdminOrManager && viewMode === 'overview' ? handleBranchHover : undefined;

  const tableOnBrokerHover: ((email: string) => void) | undefined =
    isAdminOrManager && viewMode === 'overview' ? handleBrokerHover : undefined;

  // ── Reset handlers per tab ────────────────────────────────────────────────

  const handleOverviewReset = () => {
    setSearchParams(
      (p) => {
        p.delete('branch');
        p.delete('period');
        p.delete('broker');
        p.delete('market');
        p.delete('business_type');
        p.delete('detail_type');
        p.delete('detail_id');
        return p;
      },
      { replace: true },
    );
    setPerfSortKey('score');
    setPerfSortDir('desc');
  };

  const handleAggregateReset = () => {
    setSelectedIds(new Set());
    setSearchParams(
      (p) => {
        p.delete('branch');
        p.delete('period');
        p.delete('broker');
        p.delete('market');
        p.delete('business_type');
        p.delete('detail_type');
        p.delete('detail_id');
        return p;
      },
      { replace: true },
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box bg={bgColor} minH="100vh" p="20px">
      <AnalyticsHeader viewLabel={viewLabel} />

      <AnalyticsFilters
        yearMonth={yearMonth}
        onYearMonthChange={setYearMonth}
        branchOptions={branchOptions}
        selectedBranchId={selectedBranchId}
        onBranchChange={isAdminOrManager ? handleBranchSelect : undefined}
        brokerOptions={brokerOptions}
        selectedBrokerEmail={selectedBrokerEmail}
        onBrokerChange={isAdminOrManager ? handleBrokerFilter : undefined}
        onReset={
          viewMode === 'aggregate' ? handleAggregateReset : handleOverviewReset
        }
        period={period}
        onPeriodChange={supportsPeriod ? setPeriod : undefined}
      />

      <Tabs
        variant={'soft-rounded' as const}
        colorScheme={'brand' as const}
        index={outerTabIndex}
        onChange={setOuterTabIndex}
      >
        <TabList mb="20px">
          <Tab>Overview</Tab>
          <Tab>Sales</Tab>
          <Tab isDisabled opacity={0.4}>
            Service
          </Tab>
          <Tab isDisabled opacity={0.4}>
            Book of Business
          </Tab>
        </TabList>

        <TabPanels>
          {/* ── Tab 0: Overview ── */}
          <TabPanel p={0}>
            <AnalyticsDashboardOverview
              yearMonth={yearMonth}
              period={period}
              selectedBranchId={selectedBranchId}
              selectedBrokerEmail={selectedBrokerEmail}
              brokerTable={filteredTable}
              onBranchClick={tableOnBranchClick}
              onBrokerClick={tableOnBrokerClick}
            />
          </TabPanel>

          {/* ── Tab 1: Sales ── */}
          <TabPanel p={0}>
            {/* ── Inner sub-tabs: Performance / Leads / Call List ── */}
            <Tabs
              index={salesTabIndex}
              onChange={setSalesTabIndex}
              variant="line"
              colorScheme="brand"
              isLazy
            >
              <TabList mb="20px" borderBottomColor="gray.200">
                <Tab fontSize="sm" fontWeight="600" px="16px">
                  Performance
                </Tab>
                <Tab fontSize="sm" fontWeight="600" px="16px">
                  Leads
                </Tab>
                <Tab fontSize="sm" fontWeight="600" px="16px">
                  Call List
                </Tab>
              </TabList>

              <TabPanels>
                {/* ── Tab 0: Overview ── */}
                <TabPanel p={0}>
                  {/* Overview / Compare toggle */}
                  {isAdminOrManager && (
                    <Box mb="16px">
                      <ModeToggle
                        viewMode={viewMode}
                        onOverview={() => {
                          setViewMode('overview');
                          setSelectedIds(new Set());
                        }}
                        onAggregate={() => {
                          setViewMode('aggregate');
                          setDetailTarget(null);
                        }}
                      />
                    </Box>
                  )}

                  {/* KPI cards or Aggregate bar */}
                  <Box mb="24px">
                    {salesLoading && <LoadingCard height="60px" />}
                    {!salesLoading && salesError && (
                      <ErrorAlert
                        title="Failed to load analytics"
                        description={String(salesError)}
                      />
                    )}
                    {!salesLoading &&
                      !salesError &&
                      viewMode === 'overview' &&
                      kpiData && (
                        <OverallKPICards
                          data={kpiData}
                          label={
                            selectedBrokerRow?.label ?? selectedBranchRow?.label
                          }
                        />
                      )}
                    {!salesLoading &&
                      !salesError &&
                      viewMode === 'aggregate' && (
                        <>
                          <AggregateKPIBar
                            kpi={aggregateKPI}
                            chips={aggregateChips}
                            onRemove={handleRemoveChip}
                          />
                          {aggregateBrokerRows.length > 0 && (
                            <Card mb="24px" p="20px">
                              <AggregateBrokerTable
                                rows={aggregateBrokerRows}
                                month={yearMonth}
                                onBrokerClick={(email) =>
                                  setDetailTarget({ type: 'broker', id: email })
                                }
                              />
                            </Card>
                          )}
                        </>
                      )}
                  </Box>

                  {/* Trends + entity detail panel — always shown in Overview; shown in Aggregate when a broker is selected */}
                  {(viewMode === 'overview' || detailTarget !== null) && (
                    <DetailPanel
                      target={detailTarget}
                      month={yearMonth}
                      defaultTrends={salesData?.trends ?? []}
                      defaultTrendsScope={salesData?.trends_scope ?? 'BIG'}
                      isAdmin={user?.role === ROLE_ADMIN}
                      onClear={() => setDetailTarget(null)}
                      onTargetChange={(t) => {
                        if (t.type === 'branch' && tableOnBranchClick)
                          tableOnBranchClick(Number(t.id));
                        else if (t.type === 'broker' && tableOnBrokerClick)
                          tableOnBrokerClick(t.id);
                        else setDetailTarget(t);
                      }}
                    />
                  )}

                  {/* Branch / Broker performance accordion */}
                  <Card mb="0" p="20px">
                    {salesLoading ? (
                      <LoadingCard />
                    ) : (
                      <BranchBrokerTable
                        data={filteredTable}
                        isLoading={salesLoading}
                        viewLabel={viewLabel}
                        sortKey={perfSortKey}
                        sortDir={perfSortDir}
                        onSortChange={(key, dir) => {
                          setPerfSortKey(key);
                          setPerfSortDir(dir);
                        }}
                        aggregateMode={viewMode === 'aggregate'}
                        selectedIds={
                          viewMode === 'aggregate' ? selectedIds : undefined
                        }
                        onSelectionChange={
                          viewMode === 'aggregate' ? setSelectedIds : undefined
                        }
                        onBranchClick={tableOnBranchClick}
                        onBrokerClick={tableOnBrokerClick}
                        onBranchHover={tableOnBranchHover}
                        onBrokerHover={tableOnBrokerHover}
                        activeBranchId={activeBranchIdForTable}
                        selectedBranchId={selectedBranchId}
                        month={yearMonth}
                        hideExpandedContent={detailTarget?.type === 'branch'}
                      />
                    )}
                  </Card>
                </TabPanel>

                {/* ── Tab 1: Leads ── */}
                <TabPanel p={0}>
                  <LeadManagementPanel
                    key={yearMonth}
                    data={leadMgmtData}
                    isLoading={leadMgmtLoading}
                    selectedBranchId={selectedBranchId}
                    selectedBrokerEmail={selectedBrokerEmail}
                    trends={leadTrendData?.trends ?? []}
                    onBranchClick={
                      isAdminOrManager ? handleBranchSelect : undefined
                    }
                    onBrokerClick={
                      isAdminOrManager ? handleBrokerFilter : undefined
                    }
                  />
                </TabPanel>

                {/* ── Tab 2: Call List ── */}
                <TabPanel p={0}>
                  <Card p="20px">
                    {callListLoading && <LoadingCard />}
                    {!callListLoading && callListError && (
                      <ErrorAlert title="Failed to load call list" />
                    )}
                    {!callListLoading && !callListError && (
                      <CallListTable
                        overall={callListData?.overall}
                        trends={callListData?.trends ?? []}
                        data={filteredCallList}
                        onBranchClick={
                          isAdminOrManager ? handleBranchSelect : undefined
                        }
                        onBrokerClick={
                          isAdminOrManager ? handleBrokerFilter : undefined
                        }
                      />
                    )}
                  </Card>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </TabPanel>

          <TabPanel>
            <Card p="40px" textAlign={'center' as const}>
              <p style={{ color: '#A0AEC0', fontSize: '1.125rem' }}>
                Service metrics — coming soon
              </p>
            </Card>
          </TabPanel>

          <TabPanel>
            <Card p="40px" textAlign={'center' as const}>
              <p style={{ color: '#A0AEC0', fontSize: '1.125rem' }}>
                Book of Business metrics — coming soon
              </p>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

const AnalyticsView = () => {
  const isAnalyticsEnabled = useFeatureFlag('enable-analytics-dashboard');
  if (!isAnalyticsEnabled) {
    return <ComingSoon />;
  }
  return <AnalyticsViewContent />;
};

export default AnalyticsView;
